import React from 'react';
import { Link } from 'react-router-dom';
import { Camera, ChefHat, Moon } from 'lucide-react';

import AwardBadges from '../../components/public/AwardBadges';
import PublicLayout from '../../components/public/PublicLayout';
import SmartImage from '../../components/public/SmartImage';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { aboutStory, business, heroImage } from '../../content/siteContent.mjs';

const HOW_WE_BAKE = [
  {
    icon: ChefHat,
    title: 'Made from scratch',
    body: 'Brioche and buttermilk doughs mixed in-house, jams cooked in our own pans, no mixes and no premade shells.',
  },
  {
    icon: Moon,
    title: 'Baked overnight to order',
    body: 'We only fry what has been ordered, the night before delivery — which is why nothing arrives a day old.',
  },
  {
    icon: Camera,
    title: 'Delivered with a record',
    body: 'Every drop is photographed and logged in the partner portal, alongside invoices and order history.',
  },
];

const About = () => (
  <PublicLayout testId="public-about">
    <section className="pt-16 pb-14 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-amber-50 to-white">
      <div className="max-w-4xl mx-auto text-center">
        <p className="mb-4 text-sm font-semibold uppercase tracking-widest text-amber-600">
          {business.tagline}
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
          About MAD Donuts
        </h1>
        <p className="text-lg text-gray-600 leading-relaxed">
          A two-time Best of Westchester winner baking wholesale in {business.baseTown}.
        </p>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-5">
          {aboutStory.map((paragraph) => (
            <p key={paragraph.slice(0, 32)} className="text-base sm:text-lg text-gray-600 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
        <SmartImage
          image={heroImage}
          sizes="(max-width: 1024px) 100vw, 50vw"
          className="h-[320px] sm:h-[440px] w-full rounded-2xl object-cover shadow-xl"
        />
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-amber-50/60">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Awards</h2>
        <p className="text-lg text-gray-600 mb-10">
          Voted Best Donuts in Westchester Magazine’s Best of Westchester awards — twice.
        </p>
        <div className="flex justify-center">
          <AwardBadges />
        </div>
      </div>
    </section>

    <section className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-12 text-center">How we bake</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {HOW_WE_BAKE.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="p-8 hover:shadow-xl transition-shadow duration-300">
              <div className="mb-6 inline-flex rounded-full bg-amber-100 p-4">
                <Icon size={26} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
            </Card>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5">Work with the bakery</h2>
        <p className="text-lg text-gray-300 mb-9">
          Wholesale supply for cafés and restaurants, catering for everything else.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" className="bg-amber-500 text-gray-900 hover:bg-amber-400 text-lg px-8 py-6">
            <Link to="/wholesale#inquiry" data-testid="about-wholesale-cta">Start a wholesale inquiry</Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-6"
          >
            <Link to="/menu">See what we bake</Link>
          </Button>
        </div>
      </div>
    </section>
  </PublicLayout>
);

export default About;
